import 'dayjs/locale/uz-latn';
import 'dayjs/locale/ru';

/**
 * Oy ("forMonth") bilan ishlash — panel bo'ylab YAGONA manba (T9).
 *
 * MUAMMO: bazada `Payment.forMonth` / `Expense.forMonth` aralash formatda yozilgan
 * ("2026-5" va "2026-07" birga), shu sababli jadvalda ikki xil ko'rinish chiqar va
 * oy filtri ba'zi qatorlarni topmasdi.
 *
 * QOIDA (backend `src/lib/dates.ts` dagi `normalizeMonth` bilan AYNI): serverga
 * yuboriladigan va ekranda ko'rsatiladigan qiymat DOIM "YYYY-MM" (oy ikki xonali).
 * Eski qatorlar tuzatuvchi skript ishlagunga qadar shu yerda ko'rinish darajasida
 * normallashtiriladi — foydalanuvchi hech qachon "2026-5" ni ko'rmaydi.
 */

/** Ikki xonali oy bilan "YYYY-MM" yasaydi; oy 1..12 dan tashqarida bo'lsa — bo'sh satr. */
function build(year: string, month: string): string {
  const m = Number(month);
  if (!Number.isInteger(m) || m < 1 || m > 12) return '';
  return `${year}-${String(m).padStart(2, '0')}`;
}

/**
 * Har qanday xom qiymatni "YYYY-MM" ga keltiradi.
 * Qabul qiladi: "YYYY-M", "YYYY-MM", "YYYY-MM-DD", "MM.YYYY", "MM/YYYY", "YYYYMM".
 * Tushunarsiz yoki bo'sh qiymat — bo'sh satr (chaqiruvchi "—" ko'rsatadi yoki
 * filtrni umuman qo'llamaydi).
 */
export function normalizeMonth(raw?: string | null): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const iso = /^(\d{4})-(\d{1,2})(?:-\d{1,2})?$/.exec(s);
  if (iso) return build(iso[1], iso[2]);
  const dotted = /^(\d{1,2})[./-](\d{4})$/.exec(s);
  if (dotted) return build(dotted[2], dotted[1]);
  const compact = /^(\d{4})(\d{2})$/.exec(s);
  if (compact) return build(compact[1], compact[2]);
  return '';
}

/**
 * Lokal (Toshkent) sanadan "YYYY-MM". `toISOString()` ATAYLAB ishlatilmaydi —
 * UTC+5 da oyning 1-kuni oldingi oyga siljib ketardi.
 */
export function ym(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "YYYY-MM" (joriy oy) — oy tanlagichlarning standart qiymati. */
export function thisMonth(): string {
  return ym(new Date());
}

/** "YYYY-MM" → `new Date` (oyning 1-kuni, lokal). Yaroqsiz qiymatda — null. */
export function monthToDate(m?: string | null): Date | null {
  const n = normalizeMonth(m);
  if (!n) return null;
  const [y, mm] = n.split('-');
  return new Date(Number(y), Number(mm) - 1, 1);
}

/**
 * "YYYY-MM" → ko'rsatiladigan yorliq ("Avg 2026"). `monthNames` — i18n
 * `monthsShort` massivi (12 element). Tushunarsiz qiymat xom holda qaytariladi,
 * bo'sh qiymat — bo'sh satr.
 */
export function monthLabel(m: string | null | undefined, monthNames: string[]): string {
  const n = normalizeMonth(m);
  if (!n) return m ? String(m) : '';
  const [y, mm] = n.split('-');
  const name = monthNames?.[Number(mm) - 1];
  return name ? `${name} ${y}` : n;
}

/**
 * Joriy oydan orqaga `count` ta oy ("YYYY-MM", eng yangisi birinchi) —
 * "qaysi oy uchun" tanlagichlari uchun.
 */
export function recentMonths(count: number, base: Date = new Date()): string[] {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
    return ym(d);
  });
}

/**
 * Grafik/hisobot yil tanlagichi uchun: joriy yil + oldingi (`count-1`) yil
 * ("YYYY", eng yangisi birinchi). Hisobotlar va To'lovlar sahifasidagi "Oylik
 * tushum" grafigi AYNI ro'yxatni ishlatadi — avval har sahifada nusxasi bor edi
 * va biri o'zgarganda ikkinchisi ortda qolardi.
 */
export function recentYears(count = 5, base: Date = new Date()): string[] {
  return Array.from({ length: count }, (_, i) => String(base.getFullYear() - i));
}

/**
 * Bir necha oy — chek uchun bitta satr: yil har guruh oxirida bir marta.
 *   ["2026-09"]                      → "Sentabr 2026"
 *   ["2026-10","2026-11","2026-12"]  → "Oktabr, Noyabr, Dekabr 2026"
 *   ["2026-12","2027-01"]            → "Dekabr 2026, Yanvar 2027"
 */
export function monthsListLabel(list: (string | null | undefined)[], monthNames: string[]): string {
  const norm = [...new Set(list.map((m) => normalizeMonth(m)).filter((m): m is string => !!m))].sort();
  if (!norm.length) return '';
  const groups: { y: string; names: string[] }[] = [];
  for (const m of norm) {
    const [y, mm] = m.split('-');
    const name = monthNames?.[Number(mm) - 1] ?? mm;
    const last = groups[groups.length - 1];
    if (last && last.y === y) last.names.push(name);
    else groups.push({ y, names: [name] });
  }
  return groups.map((g) => `${g.names.join(', ')} ${g.y}`).join(', ');
}

/**
 * Mantine oy tanlagichlari (`MonthPickerInput`) uchun dayjs locale — panel tili bo'yicha:
 * uz → "uz-latn" ("Oktabr 2026"), ru → "ru", en → "en". Faqat komponentning `locale` propi
 * orqali beriladi — global `dayjs.locale()` o'zgarmaydi (boshqa sana formatlari avvalgidek).
 */
export function sanaLocale(lang?: string | null): string {
  const l = (lang ?? '').slice(0, 2);
  return l === 'ru' ? 'ru' : l === 'en' ? 'en' : 'uz-latn';
}

/** Oy tanlagich qiymati: "Oktabr 2026" — `monthLabel` kabi avval oy, keyin yil. */
export const OY_FORMATI = 'MMMM YYYY';
